import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Typography, Box, Fade, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, Stack, Grid } from '@mui/material';
import type { RootState, PlayerScore } from '../../types/store';
import AppActions from '../../actions/app-actions';
import AppStore from '../../stores/app-store';
import logoIndexImage from '../../assets/logo.svg';
import Player from '../../components/player';
import Glass from "../../components/glass/glass";
import AppUtils from "../../utils/app-utils";
import { CirclePlus, Play, Instagram, Facebook } from "lucide-react";
import UIIcon from '../../components/ui-icon';

const STATE_THANKS_FADEIN = 0;
const STATE_THANKS_FADEOUT = 1;
const STATE_LEADERBOARD_FADEIN = 2;
const STATE_REVEAL_PLAYERS = 3;

const TIMER_THANKS_HOLD = 2000;
const TIMER_THANKS_FADEOUT = 1000;
const TIMER_LEADERBOARD_FADEIN = 1000;

function getTableHeader(str: string, width: string, isTV: boolean, align: 'left' | 'right' = 'left') {
    return (
        <TableCell
            align={align}
            sx={{
                color: "rgba(255, 255, 255, 0.65)",
                fontWeight: 700,
                fontSize: isTV ? "clamp(0.7rem, 0.8vw, 0.82rem)" : "0.78rem",
                letterSpacing: "0.08em",
                width: width,
                borderBottom: "1px solid rgba(255,255,255,0.12)",
                py: isTV ? 0.6 : 0.8,
                px: { xs: 1, sm: 1.5, md: 2 },
                whiteSpace: 'nowrap',
            }}
        >
            {str}
        </TableCell>
    );
}

function FadeWithExit({
    step,
    visibleStep,
    logoLoaded = true,
    children
}: {
    step: number,
    visibleStep: number,
    logoLoaded?: boolean,
    children: React.ReactNode
}) {
    const shouldRender = step === visibleStep || step === visibleStep + 1 || (visibleStep === STATE_LEADERBOARD_FADEIN && step >= visibleStep);
    const isVisible = (step === visibleStep || (visibleStep === STATE_LEADERBOARD_FADEIN && step >= visibleStep)) && logoLoaded;

    return shouldRender ? (
        <Fade in={isVisible} timeout={1000}>
            <div style={{ height: '100%', width: '100%' }}>{children}</div>
        </Fade>
    ) : null;
}

export default function LobbyDone() {
    const lobby = useSelector((state: RootState) => state.page.lobby);
    const translate = useSelector((state: RootState) => state.ui.language.lobby.done);
    const [hasClickedRematch, setHasClickedRematch] = useState(false);
    const isTV = !lobby.playing;

    const [step, setStep] = useState(STATE_THANKS_FADEIN);
    const [displayIndex, setDisplayIndex] = useState(0);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const imgRef = useRef<HTMLImageElement>(null);

    useEffect(() => {
        if (imgRef.current && imgRef.current.complete) {
            setLogoLoaded(true);
        }
    }, []);

    const isMobile = AppUtils.isMobile();

    const scoreboard: PlayerScore[][] = useMemo(() => {
        const scores = new Map<number, PlayerScore[]>();
        lobby.done.players.forEach(player => {
            const playersWithScore = scores.get(player.score) || [];
            playersWithScore.push(player);
            scores.set(player.score, playersWithScore);
        });

        return Array.from(scores.entries())
            .sort((a, b) => b[0] - a[0])
            .map(entry => entry[1]);
    }, [lobby.done.players]);

    const flatScoreboard = useMemo(() => {
        const list: { rank: number; player: PlayerScore; rankIndex: number }[] = [];
        scoreboard.forEach((playerGroup, rankIndex) => {
            playerGroup.forEach((player) => {
                list.push({ rank: rankIndex + 1, player, rankIndex });
            });
        });
        return list;
    }, [scoreboard]);

    const [buttonsVisible, setButtonsVisible] = useState(false);

    // 1 column for <= 6 players, 2 columns for 7-12 players, 3 columns for 13-20 players, 4 columns for 21+ players
    const numColumns = isMobile
        ? 1
        : flatScoreboard.length > 20
            ? 4
            : flatScoreboard.length > 12
                ? 3
                : flatScoreboard.length > 6
                    ? 2
                    : 1;

    const columnsData = useMemo(() => {
        if (numColumns === 4) {
            const perCol = Math.ceil(flatScoreboard.length / 4);
            return [
                flatScoreboard.slice(0, perCol),
                flatScoreboard.slice(perCol, perCol * 2),
                flatScoreboard.slice(perCol * 2, perCol * 3),
                flatScoreboard.slice(perCol * 3),
            ];
        }
        if (numColumns === 3) {
            const perCol = Math.ceil(flatScoreboard.length / 3);
            return [
                flatScoreboard.slice(0, perCol),
                flatScoreboard.slice(perCol, perCol * 2),
                flatScoreboard.slice(perCol * 2),
            ];
        }
        if (numColumns === 2) {
            const perCol = Math.ceil(flatScoreboard.length / 2);
            return [
                flatScoreboard.slice(0, perCol),
                flatScoreboard.slice(perCol),
            ];
        }
        return [flatScoreboard];
    }, [flatScoreboard, numColumns]);

    // Initial sequential animation effect
    useEffect(() => {
        if (!logoLoaded) return;

        let t2: NodeJS.Timeout | undefined = undefined;
        let t3: NodeJS.Timeout | undefined = undefined;

        const t1 = setTimeout(() => {
            setStep(STATE_THANKS_FADEOUT);

            t2 = setTimeout(() => {
                setStep(STATE_LEADERBOARD_FADEIN);

                t3 = setTimeout(() => {
                    setStep(STATE_REVEAL_PLAYERS);
                }, TIMER_LEADERBOARD_FADEIN);

            }, TIMER_THANKS_FADEOUT);

        }, TIMER_THANKS_HOLD);

        return () => {
            clearTimeout(t1);
            if (t2) clearTimeout(t2);
            if (t3) clearTimeout(t3);
        };
    }, [logoLoaded]);

    // Fast reveal animation adapting to total player count
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        if (step === STATE_REVEAL_PLAYERS) {
            const revealIntervalMs = Math.max(70, Math.min(500, Math.floor(3000 / Math.max(1, scoreboard.length))));

            interval = setInterval(() => {
                setDisplayIndex(prev => {
                    if (prev >= scoreboard.length) {
                        if (interval) clearInterval(interval);
                        return prev;
                    }
                    return prev + 1;
                });
            }, revealIntervalMs);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [step, scoreboard.length]);

    // Reset buttons when animation restarts or hasn't reached reveal
    useEffect(() => {
        if (step < STATE_REVEAL_PLAYERS) {
            setButtonsVisible(false);
        }
    }, [step]);

    // Reveal buttons shortly after the final #1 rank has been revealed
    useEffect(() => {
        if (step === STATE_REVEAL_PLAYERS && displayIndex >= scoreboard.length && scoreboard.length > 0) {
            const timer = setTimeout(() => {
                setButtonsVisible(true);
            }, 600);
            return () => clearTimeout(timer);
        }
    }, [step, displayIndex, scoreboard.length]);

    function onLeave() {
        AppStore.dispatch(AppActions.LeaveLobby());
    }

    function onRematch() {
        if (lobby.admin) {
            AppStore.dispatch(AppActions.RematchLobby({ code: lobby.code }));
        } else {
            setHasClickedRematch(true);
        }
    }

    const logoDisplay = (
        <Box>
            <div className="logo-container">
                <img
                    ref={imgRef}
                    src={logoIndexImage}
                    alt="Logo"
                    onLoad={() => setLogoLoaded(true)}
                    onError={() => setLogoLoaded(true)}
                />
            </div>
            <Typography variant="h6">{translate.thanksForPlaying}</Typography>
            {lobby.playing && (
                <div style={{ color: "#6e707b", textAlign: "left", display: 'inline-block', marginTop: '16px' }}>
                    {translate.followUs}<br />
                    <Stack direction="row" gap={1} alignItems="center" justifyContent="center" mt={1}>
                        <UIIcon icon={Instagram} /> <UIIcon icon={Facebook} /> @moesbeergame
                    </Stack>
                </div>
            )}
        </Box>
    );

    function renderTable(playerList: typeof flatScoreboard) {
        return (
            <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow sx={{ backgroundColor: "rgba(255,255,255,0.06)" }}>
                        {getTableHeader(translate.rank.toUpperCase(), "24%", isTV, "left")}
                        {getTableHeader(translate.nickname.toUpperCase(), "52%", isTV, "left")}
                        {getTableHeader(translate.score.toUpperCase(), "24%", isTV, "right")}
                    </TableRow>
                </TableHead>
                <TableBody sx={{ bgcolor: "transparent" }}>
                    {playerList.map(({ rank, player, rankIndex }) => (
                        <TableRow
                            key={player.nickname}
                            sx={{
                                height: isTV ? (flatScoreboard.length > 20 ? 36 : flatScoreboard.length > 12 ? 40 : 44) : 46,
                                backgroundColor: "transparent",
                                opacity: rankIndex >= scoreboard.length - displayIndex ? 1 : 0,
                                transform: rankIndex >= scoreboard.length - displayIndex ? 'translateY(0)' : 'translateY(-8px)',
                                transition: 'opacity 350ms cubic-bezier(0.4, 0, 0.2, 1), transform 350ms cubic-bezier(0.4, 0, 0.2, 1)',
                                borderBottom: "1px solid rgba(255,255,255,0.06)",
                                '&:hover': { backgroundColor: "rgba(255,255,255,0.04)" },
                            }}
                        >
                            <TableCell sx={{
                                color: rank === 1 ? "#ffd700" : rank === 2 ? "#e0e0e0" : rank === 3 ? "#cd7f32" : "#fff",
                                fontWeight: rank <= 3 ? 700 : 600,
                                py: isTV ? (flatScoreboard.length > 20 ? 0.35 : 0.5) : 0.5,
                                px: { xs: 1, sm: 1.5, md: isTV ? 1.5 : 2 },
                                fontSize: isTV ? (flatScoreboard.length > 20 ? 'clamp(0.8rem, 1vw, 0.95rem)' : 'clamp(0.85rem, 1.1vw, 1.05rem)') : '0.95rem',
                                lineHeight: 1.2,
                                whiteSpace: 'nowrap',
                                verticalAlign: 'middle',
                            }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', minHeight: isTV && flatScoreboard.length > 20 ? 24 : 28 }}>
                                    {rank === 1 ? "🥇 1" : rank === 2 ? "🥈 2" : rank === 3 ? "🥉 3" : rank}
                                </Box>
                            </TableCell>
                            <TableCell sx={{
                                color: "#fff",
                                py: isTV ? (flatScoreboard.length > 20 ? 0.35 : 0.5) : 0.5,
                                px: { xs: 1, sm: 1.5, md: isTV ? 1.5 : 2 },
                                fontSize: isTV ? (flatScoreboard.length > 20 ? 'clamp(0.8rem, 1vw, 0.95rem)' : 'clamp(0.85rem, 1.1vw, 1.05rem)') : '0.95rem',
                                lineHeight: 1.2,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'middle',
                            }}>
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', minHeight: isTV && flatScoreboard.length > 20 ? 24 : 28, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                                    <Player player={player.nickname} />
                                </Box>
                            </TableCell>
                            <TableCell
                                align="right"
                                sx={{
                                    color: "#90caf9",
                                    fontWeight: 600,
                                    py: isTV ? (flatScoreboard.length > 20 ? 0.35 : 0.5) : 0.5,
                                    px: { xs: 1, sm: 1.5, md: isTV ? 1.5 : 2 },
                                    fontSize: isTV ? (flatScoreboard.length > 20 ? 'clamp(0.8rem, 1vw, 0.95rem)' : 'clamp(0.85rem, 1.1vw, 1.05rem)') : '0.95rem',
                                    lineHeight: 1.2,
                                    whiteSpace: 'nowrap',
                                    verticalAlign: 'middle',
                                }}
                            >
                                <Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', minHeight: isTV && flatScoreboard.length > 20 ? 24 : 28, width: '100%' }}>
                                    {player.score}
                                </Box>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        );
    }

    return (
        <>
            <FadeWithExit step={step} visibleStep={STATE_THANKS_FADEIN} logoLoaded={logoLoaded}>
                <Box
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    sx={{ height: "100dvh", width: "100%", textAlign: "center", position: "absolute", top: 0, left: 0 }}
                >
                    {logoDisplay}
                </Box>
            </FadeWithExit>

            <FadeWithExit step={step} visibleStep={STATE_LEADERBOARD_FADEIN}>
                <Box
                    sx={{
                        height: "100dvh",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                        px: { xs: 1.5, md: 3 },
                        py: isTV ? 1 : 1.5,
                        position: "relative",
                        zIndex: 10,
                    }}
                >
                    <Typography
                        variant={isTV ? "h4" : "h3"}
                        fontWeight={700}
                        mb={isTV ? 1.5 : 2.5}
                        sx={{ fontSize: isTV ? 'clamp(1.5rem, 2.4vw, 2.2rem)' : 'clamp(1.4rem, 3vw, 2.2rem)' }}
                    >
                        🏆 {translate.leaderboard.toUpperCase()} 🏆
                    </Typography>

                    <Glass
                        sx={{
                            width: isMobile
                                ? "96%"
                                : numColumns === 4
                                    ? "98%"
                                    : numColumns === 3
                                        ? "95%"
                                        : numColumns === 2
                                            ? "88%"
                                            : "72%",
                            maxWidth: isMobile
                                ? "100%"
                                : numColumns === 4
                                    ? 1850
                                    : numColumns === 3
                                        ? 1650
                                        : numColumns === 2
                                            ? 1250
                                            : 960,
                            margin: "0 auto",
                            borderRadius: 3,
                            p: { xs: 1.5, sm: 2, md: isTV ? 2 : 3 },
                            position: "relative",
                            display: 'flex',
                            flexDirection: 'column',
                            maxHeight: isTV ? 'calc(100dvh - 75px)' : 'calc(100dvh - 120px)',
                            boxShadow: "0 16px 48px rgba(0,0,0,0.45)",
                        }}
                    >
                        <TableContainer
                            sx={{
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                flex: 1,
                                minHeight: 0,
                                pb: isTV && buttonsVisible ? 5.5 : 0,
                                transition: 'padding-bottom 300ms ease',
                                scrollbarColor: "#7dabd0ff rgba(255,255,255,0.1)",
                                scrollbarWidth: "thin",
                                '&::-webkit-scrollbar': { width: '6px' },
                                '&::-webkit-scrollbar-thumb': { background: '#7dabd0ff', borderRadius: '4px' },
                                '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.08)' },
                            }}
                        >
                            {numColumns === 4 ? (
                                <Grid container spacing={1.5}>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        {renderTable(columnsData[0])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        {renderTable(columnsData[1])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        {renderTable(columnsData[2])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                        {renderTable(columnsData[3])}
                                    </Grid>
                                </Grid>
                            ) : numColumns === 3 ? (
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                                        {renderTable(columnsData[0])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                                        {renderTable(columnsData[1])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                                        {renderTable(columnsData[2])}
                                    </Grid>
                                </Grid>
                            ) : numColumns === 2 ? (
                                <Grid container spacing={2.5}>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        {renderTable(columnsData[0])}
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                        {renderTable(columnsData[1])}
                                    </Grid>
                                </Grid>
                            ) : (
                                renderTable(columnsData[0])
                            )}
                        </TableContainer>

                        {/* Rematch & Leave Buttons: Fade in as a floating overlay on TV, or standard footer on mobile */}
                        <Fade in={buttonsVisible} timeout={800}>
                            <Box
                                sx={{
                                    position: isTV ? 'absolute' : 'relative',
                                    bottom: isTV ? 12 : 'auto',
                                    left: 0,
                                    right: 0,
                                    mt: isTV ? 0 : { xs: 1.5, md: 2.5 },
                                    display: 'flex',
                                    justifyContent: 'center',
                                    pointerEvents: buttonsVisible ? 'auto' : 'none',
                                    zIndex: 25,
                                }}
                            >
                                <Box
                                    sx={{
                                        background: isTV ? 'rgba(15, 20, 32, 0.88)' : 'transparent',
                                        backdropFilter: isTV ? 'blur(16px)' : 'none',
                                        border: isTV ? '1px solid rgba(255, 255, 255, 0.18)' : 'none',
                                        borderRadius: 3,
                                        py: isTV ? 1 : 0,
                                        px: isTV ? 2.5 : 0,
                                        boxShadow: isTV ? '0 8px 32px rgba(0, 0, 0, 0.55)' : 'none',
                                    }}
                                >
                                    <Stack direction="row" gap={2} flexWrap="wrap" justifyContent="center">
                                        <Button
                                            color="secondary"
                                            startIcon={<Play size={20} />}
                                            variant="contained"
                                            onClick={onLeave}
                                        >
                                            {translate.leaveLobby}
                                        </Button>
                                        <Button
                                            variant="contained"
                                            startIcon={<CirclePlus size={20} />}
                                            onClick={onRematch}
                                            disabled={!lobby.admin && hasClickedRematch}
                                        >
                                            {lobby.admin
                                                ? translate.playAgain
                                                : !hasClickedRematch
                                                    ? translate.playAgain
                                                    : translate.playAgainWaiting}
                                        </Button>
                                    </Stack>
                                </Box>
                            </Box>
                        </Fade>
                    </Glass>
                </Box>
            </FadeWithExit>
        </>
    );
}
